import { Fragment, useEffect, useState, useRef } from 'react'
import { H3 } from '../../../../AbstractElements'
import { Button, Card, CardBody, Col, Container, Form, FormGroup, Input, Label, Row, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap'
import Select from 'react-select';
import axios from 'axios';
import {productBaseURL } from '../../../../Services/api/baseURL';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Swal from 'sweetalert2';
import { useNavigate, useParams } from 'react-router';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

function CreateSpecial() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [productData, setProductData] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [token, setToken] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    // Separate states for website and mobile banners
    const [websiteBannerPreview, setWebsiteBannerPreview] = useState(null);
    const [websiteBannerFile, setWebsiteBannerFile] = useState(null);
    const [mobileBannerPreview, setMobileBannerPreview] = useState(null);
    const [mobileBannerFile, setMobileBannerFile] = useState(null);

    // Image cropping states
    const [cropModalOpen, setCropModalOpen] = useState(false);
    const [currentCropType, setCurrentCropType] = useState(null); // 'website' or 'mobile'
    const [imageToCrop, setImageToCrop] = useState(null);
    const [crop, setCrop] = useState({ unit: '%', width: 90, aspect: 11/1 });
    const [completedCrop, setCompletedCrop] = useState(null);
    const imgRef = useRef(null);
    const previewCanvasRef = useRef(null);

    const isDarkMode = localStorage.getItem("mix_background_layout")

    const lightModeColors = {
        primary: '#007bff',
        secondary: '#6c757d',
        text: '#212529',
        background: '#ffffff',
    };

    const darkModeColors = {
        primary: '#0d6efd',
        secondary: '#3F444D',
        text: 'white',
        background: '#262932',
    };

    const colors = (isDarkMode === 'dark-only') ? darkModeColors : lightModeColors;

    const customStyles = {
        control: (provided) => ({
            ...provided,
            backgroundColor: colors.background,
            borderColor: colors.secondary,
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected ? colors.primary : colors.background,
            color: state.isSelected ? colors.text : colors.text,
        }),
    };

    useEffect(() => {
        UserData();
        // Safety net: if a previous page/modal left the body scroll-locked
        // (a known reactstrap + SweetAlert2 interaction issue), clear it here.
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
        document.body.classList.remove('modal-open');
        document.documentElement.classList.remove('swal2-height-auto', 'swal2-shown');
    }, [])

    const UserData = async () => {
        const token = await JSON.parse(localStorage.getItem('token'));
        setToken(token)
    }

    const validationSchema = Yup.object().shape({
        name: Yup.string().required('Special Offer Name is required'),
        description: Yup.string(),
        validFrom: Yup.date().required('Valid From date is required'),
        validTo: Yup.date()
            .required('Valid To date is required')
            .min(Yup.ref('validFrom'), 'Valid To date must be after Valid From date'),
    });

    const formik = useFormik({
        initialValues: {
            name: "",
            description: "",
            validFrom: new Date(),
            validTo: new Date(),
            products: [],
            status: "active",
        },
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            try {
                if (new Date(values.validFrom) >= new Date(values.validTo)) {
                    Swal.fire({
                        icon: 'error',
                        title: 'Valid To date must be after Valid From date',
                    });
                    return;
                }

                setIsLoading(true);

                // Create FormData for file upload
                const formData = new FormData();
                formData.append('name', values.name);
                formData.append('description', values.description);
                formData.append('validFrom', values.validFrom);
                formData.append('validTo', values.validTo);
                formData.append('status', values.status);

                if (values.products && values.products.length > 0) {
                    // Each selected option carries { value: variantId, productId, label }
                    // Backend expects [{ product, variant }] pairs.
                    const productVariantPairs = values.products.map(item => ({
                        product: item.productId,
                        variant: item.value,
                    }));
                    formData.append('products', JSON.stringify(productVariantPairs));
                }

                // Append website banner if exists (new file uploaded)
                if (websiteBannerFile) {
                    formData.append('websiteBanner', websiteBannerFile);
                }

                // Append mobile banner if exists (new file uploaded)
                if (mobileBannerFile) {
                    formData.append('mobileBanner', mobileBannerFile);
                }

                let res;
                if (id) {
                    res = await axios.patch(`${productBaseURL}/products/specials/${id}`, formData, {
                        headers: {
                            Authorization: `${token}`,
                            'Content-Type': 'multipart/form-data'
                        }
                    });
                } else {
                    res = await axios.post(`${productBaseURL}/products/specials`, formData, {
                        headers: {
                            Authorization: `${token}`,
                            'Content-Type': 'multipart/form-data'
                        }
                    });
                }

                if (res?.status === 200 || res?.status === 201) {
                    Swal.fire({
                        icon: "success",
                        title: res?.data?.message || (id ? "Special Offer updated successfully" : "Special Offer created successfully")
                    })
                    navigate(`/special`);
                    formik.resetForm();
                    setSelectedProducts([]);
                    setWebsiteBannerPreview(null);
                    setWebsiteBannerFile(null);
                    setMobileBannerPreview(null);
                    setMobileBannerFile(null);
                    setIsLoading(false);
                }
            }
            catch (error) {
                Swal.fire({
                    icon: "error",
                    title: error?.response?.data?.message || "An error occurred"
                })
                console.error(error);
                setIsLoading(false);
            }
        }
    });

    // Fetches flattened variant options (one per variant) for the select.
    const fetchProductList = async () => {
        const token = await JSON.parse(localStorage.getItem("token"))
        try {
            const response = await axios.get(`${productBaseURL}/products/specials/product-options`, {
                headers: {
                    Authorization: `${token}`,
                }
            })
            if (response?.data?.success) {
                // Each option: { value: variantId, productId, label, price, image }
                setProductData(response.data.data);
            }
        } catch (error) {
            console.log(error)
        }
    }

    useEffect(() => {
        fetchProductList();

        if (id) {
            getEditOfferData();
        }
    }, [id]);

    const handleProductsChange = (newValue) => {
        setSelectedProducts(newValue || []);
        formik.setFieldValue('products', newValue || []);
    };

    const validateImageAspectRatio = (file, expectedRatio, bannerType) => {
        return new Promise((resolve) => {
            const img = new Image();
            img.onload = function() {
                const aspectRatio = this.width / this.height;
                const tolerance = 0.1;

                if (Math.abs(aspectRatio - expectedRatio) > tolerance) {
                    // Show option to crop
                    Swal.fire({
                        icon: 'warning',
                        title: 'Image Dimensions Mismatch',
                        text: `Would you like to crop this image to match the required aspect ratio?`,
                        showCancelButton: true,
                        confirmButtonText: 'Yes, Crop It',
                        cancelButtonText: 'Cancel'
                    }).then((result) => {
                        if (result.isConfirmed) {
                            openCropModal(file, bannerType, expectedRatio);
                        }
                        resolve(false);
                    });
                } else {
                    resolve(true);
                }
            };
            img.src = URL.createObjectURL(file);
        });
    };

    // Reactstrap's Modal and SweetAlert2 each add/remove their own scroll-lock
    // styles/classes on <body> (and, for Swal, <html>). When a Swal fires
    // right as the Modal is transitioning closed, their cleanups can race and
    // strand a lock behind — leaving the whole page unable to scroll. Call
    // this after any Modal-close + Swal combo to guarantee a clean slate.
    const resetBodyScroll = () => {
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
        document.body.classList.remove('modal-open');
        document.documentElement.classList.remove('swal2-height-auto', 'swal2-shown');
    };

    const closeCropModal = () => {
        setCropModalOpen(false);
        setImageToCrop(null);
        setCompletedCrop(null);
        resetBodyScroll();
    };

    const openCropModal = (file, bannerType, aspectRatio) => {
        setCurrentCropType(bannerType);
        setCrop({ unit: '%', width: 90, aspect: aspectRatio });
        setImageToCrop(URL.createObjectURL(file));
        setCropModalOpen(true);
    };

    const handleWebsiteBannerChange = async (event) => {
        const file = event.target.files[0];
        if (file) {
            // Validate file size
            if (file.size > 5 * 1024 * 1024) {
                Swal.fire({
                    icon: 'error',
                    title: 'File too large',
                    text: 'Please upload an image smaller than 5MB'
                });
                event.target.value = '';
                return;
            }

            // Validate 11:1 aspect ratio for website banner or offer to crop
            const isValidRatio = await validateImageAspectRatio(file, 11, 'website');
            if (isValidRatio) {
                setWebsiteBannerFile(file);
                setWebsiteBannerPreview(URL.createObjectURL(file));
            } else {
                event.target.value = '';
            }
        }
    };

    const handleMobileBannerChange = async (event) => {
        const file = event.target.files[0];
        if (file) {
            // Validate file size
            if (file.size > 5 * 1024 * 1024) {
                Swal.fire({
                    icon: 'error',
                    title: 'File too large',
                    text: 'Please upload an image smaller than 5MB'
                });
                event.target.value = '';
                return;
            }

            // Validate 16:9 aspect ratio for mobile banner or offer to crop
            const isValidRatio = await validateImageAspectRatio(file, 16/9, 'mobile');
            if (isValidRatio) {
                setMobileBannerFile(file);
                setMobileBannerPreview(URL.createObjectURL(file));
            } else {
                event.target.value = '';
            }
        }
    };

    const onImageLoad = (e) => {
        imgRef.current = e.currentTarget;
    };

    const generateCroppedImage = async () => {
        if (!completedCrop || !imgRef.current) {
            Swal.fire({
                icon: 'error',
                title: 'Please select a crop area'
            });
            return;
        }

        const image = imgRef.current;
        const canvas = document.createElement('canvas');
        const crop = completedCrop;

        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;
        const ctx = canvas.getContext('2d');

        canvas.width = crop.width * scaleX;
        canvas.height = crop.height * scaleY;

        ctx.drawImage(
            image,
            crop.x * scaleX,
            crop.y * scaleY,
            crop.width * scaleX,
            crop.height * scaleY,
            0,
            0,
            canvas.width,
            canvas.height
        );

        return new Promise((resolve) => {
            canvas.toBlob((blob) => {
                if (!blob) {
                    console.error('Canvas is empty');
                    return;
                }
                const file = new File([blob], `cropped-${currentCropType}-${Date.now()}.jpg`, {
                    type: 'image/jpeg',
                });
                resolve(file);
            }, 'image/jpeg', 0.95);
        });
    };

    const handleCropComplete = async () => {
        try {
            const croppedFile = await generateCroppedImage();

            if (currentCropType === 'website') {
                setWebsiteBannerFile(croppedFile);
                setWebsiteBannerPreview(URL.createObjectURL(croppedFile));
            } else if (currentCropType === 'mobile') {
                setMobileBannerFile(croppedFile);
                setMobileBannerPreview(URL.createObjectURL(croppedFile));
            }

            closeCropModal();

            Swal.fire({
                icon: 'success',
                title: 'Image cropped successfully!',
                timer: 1500
            }).then(() => {
                resetBodyScroll();
            });
        } catch (error) {
            console.error('Error cropping image:', error);
            Swal.fire({
                icon: 'error',
                title: 'Failed to crop image'
            }).then(() => {
                resetBodyScroll();
            });
        }
    };

    const removeWebsiteBanner = () => {
        setWebsiteBannerFile(null);
        setWebsiteBannerPreview(null);
        const input = document.getElementById('websiteBannerInput');
        if (input) input.value = '';
    };

    const removeMobileBanner = () => {
        setMobileBannerFile(null);
        setMobileBannerPreview(null);
        const input = document.getElementById('mobileBannerInput');
        if (input) input.value = '';
    };

    const handleReset = () => {
        formik.resetForm();
        setSelectedProducts([]);
        setWebsiteBannerPreview(null);
        setWebsiteBannerFile(null);
        setMobileBannerPreview(null);
        setMobileBannerFile(null);
    }

    const getEditOfferData = async () => {
        const token = await JSON.parse(localStorage.getItem("token"))
        try {
            const response = await axios.get(`${productBaseURL}/products/specials/${id}`, {
                headers: {
                    Authorization: `${token}`,
                }
            });
            if (response?.data?.success) {
                let data = response?.data?.data;
                if (data) {
                    formik.setFieldValue("name", data.name);
                    formik.setFieldValue("description", data.description || "");
                    formik.setFieldValue("validFrom", new Date(data.validFrom));
                    formik.setFieldValue("validTo", new Date(data.validTo));
                    formik.setFieldValue("status", data.status);

                    // data.products = [{ product: {_id, productName}, variant: {_id, variantName, vol} }]
                    if (data.products && data.products.length > 0) {
                        const productOptions = data.products
                            .filter(item => item?.variant && item?.product)
                            .map(item => ({
                                value: item.variant._id,
                                productId: item.product._id,
                                label: `${item.product.productName} - ${item.variant.variantName}${
                                    item.variant.vol ? ` (${item.variant.vol})` : ''
                                }`,
                            }));
                        setSelectedProducts(productOptions);
                        formik.setFieldValue("products", productOptions);
                    }

                    // Set existing banner images
                    if (data.bannerImages?.website) {
                        setWebsiteBannerPreview(data.bannerImages.website);
                    }
                    if (data.bannerImages?.mobile) {
                        setMobileBannerPreview(data.bannerImages.mobile);
                    }
                }
            }
        } catch (error) {
            console.log(error)
        }
    }

    return (
        <Fragment>
            <Form className='theme-form offer-form' onSubmit={formik.handleSubmit}>
                <Container fluid={true}>
                    <div className='page-title'>
                        <Row>
                            <Col xs='6'>
                                <H3>{id ? "Update Special Offer" : "Add a New Special Offer"}</H3>
                            </Col>
                            <Col xs='6'>
                                <div className='text-right'>
                                    {!id && <Button onClick={() => handleReset()} className='me-3 reset_btn'>Reset All</Button>}
                                    <Button className='save_btn' type='submit' disabled={isLoading}>
                                        {!isLoading && (id ? "Update Special Offer" : "Save Special Offer")}
                                        {isLoading && (id ? "Updating..." : "Saving...")}
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </div>
                </Container>

                <Container fluid={true}>
                    <Row>
                        <Col sm='12'>
                            <Card>
                                <CardBody>
                                    <Row>
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label>Special Offer Name <span className='text-danger'>*</span></Label>
                                                <Input
                                                    type='text'
                                                    placeholder='Enter Special Offer Name'
                                                    name='name'
                                                    onChange={formik.handleChange}
                                                    value={formik.values.name}
                                                    onBlur={formik.handleBlur}
                                                />
                                                {formik.touched.name && formik.errors.name && (
                                                    <span className="error text-danger">{formik.errors.name}</span>
                                                )}
                                            </FormGroup>
                                        </Col>

                                        <Col md={6}>
                                            <FormGroup>
                                                <Label>Status</Label>
                                                <Input
                                                    type='select'
                                                    name='status'
                                                    onChange={formik.handleChange}
                                                    value={formik.values.status}
                                                    onBlur={formik.handleBlur}
                                                >
                                                    <option value="active">Active</option>
                                                    <option value="inactive">Inactive</option>
                                                </Input>
                                            </FormGroup>
                                        </Col>

                                        <Col md={6}>
                                            <FormGroup>
                                                <Label>Valid From <span className='text-danger'>*</span></Label>
                                                <DatePicker
                                                    className="form-control"
                                                    selected={formik.values.validFrom}
                                                    onChange={(date) => formik.setFieldValue('validFrom', date)}
                                                    dateFormat="dd/MM/yyyy"
                                                    minDate={new Date()}
                                                />
                                                {formik.touched.validFrom && formik.errors.validFrom && (
                                                    <span className="error text-danger">{formik.errors.validFrom}</span>
                                                )}
                                            </FormGroup>
                                        </Col>

                                        <Col md={6}>
                                            <FormGroup>
                                                <Label>Valid To <span className='text-danger'>*</span></Label>
                                                <DatePicker
                                                    className="form-control"
                                                    selected={formik.values.validTo}
                                                    onChange={(date) => formik.setFieldValue('validTo', date)}
                                                    dateFormat="dd/MM/yyyy"
                                                    minDate={formik.values.validFrom}
                                                />
                                                {formik.touched.validTo && formik.errors.validTo && (
                                                    <span className="error text-danger">{formik.errors.validTo}</span>
                                                )}
                                            </FormGroup>
                                        </Col>

                                        {/* Website Banner */}
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label>Website Banner Image (11:1 Aspect Ratio)</Label>
                                                <Input
                                                    id="websiteBannerInput"
                                                    type='file'
                                                    accept='image/*'
                                                    onChange={handleWebsiteBannerChange}
                                                    className="form-control"
                                                />
                                                <small className="form-text text-muted">
                                                    Upload website banner with 11:1 aspect ratio (e.g., 1100x100px). Max size: 5MB
                                                </small>

                                                {websiteBannerPreview && (
                                                    <div className="banner-preview mt-3">
                                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                                            <Label>Website Banner Preview:</Label>
                                                            <Button
                                                                type="button"
                                                                color="danger"
                                                                size="sm"
                                                                onClick={removeWebsiteBanner}
                                                            >
                                                                Remove
                                                            </Button>
                                                        </div>
                                                        <div
                                                            className="banner-preview-container"
                                                            style={{
                                                                width: '100%',
                                                                maxWidth: '440px',
                                                                aspectRatio: '11/1',
                                                                border: '2px dashed #ddd',
                                                                borderRadius: '8px',
                                                                overflow: 'hidden',
                                                                position: 'relative'
                                                            }}
                                                        >
                                                            <img
                                                                src={websiteBannerPreview}
                                                                alt="Website Banner Preview"
                                                                style={{
                                                                    width: '100%',
                                                                    height: '100%',
                                                                    objectFit: 'cover'
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                            </FormGroup>
                                        </Col>

                                        {/* Mobile Banner */}
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label>Mobile Banner Image (16:9 Aspect Ratio)</Label>
                                                <Input
                                                    id="mobileBannerInput"
                                                    type='file'
                                                    accept='image/*'
                                                    onChange={handleMobileBannerChange}
                                                    className="form-control"
                                                />
                                                <small className="form-text text-muted">
                                                    Upload mobile banner with 16:9 aspect ratio (e.g., 1920x1080px). Max size: 5MB
                                                </small>

                                                {mobileBannerPreview && (
                                                    <div className="banner-preview mt-3">
                                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                                            <Label>Mobile Banner Preview:</Label>
                                                            <Button
                                                                type="button"
                                                                color="danger"
                                                                size="sm"
                                                                onClick={removeMobileBanner}
                                                            >
                                                                Remove
                                                            </Button>
                                                        </div>
                                                        <div
                                                            className="banner-preview-container"
                                                            style={{
                                                                width: '100%',
                                                                maxWidth: '320px',
                                                                aspectRatio: '16/9',
                                                                border: '2px dashed #ddd',
                                                                borderRadius: '8px',
                                                                overflow: 'hidden',
                                                                position: 'relative'
                                                            }}
                                                        >
                                                            <img
                                                                src={mobileBannerPreview}
                                                                alt="Mobile Banner Preview"
                                                                style={{
                                                                    width: '100%',
                                                                    height: '100%',
                                                                    objectFit: 'cover'
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                            </FormGroup>
                                        </Col>

                                        <Col md={12}>
                                            <FormGroup className='products_div'>
                                                <Label className="col-form-label">Select Products (Variants)</Label>
                                                {/*
                                                  Switched from CreatableSelect to plain Select: every option here
                                                  must map to a real product+variant pair, so users shouldn't be
                                                  able to type/"create" an option with no productId behind it.
                                                */}
                                                <Select
                                                    className='my-select'
                                                    isMulti
                                                    options={productData}
                                                    value={selectedProducts}
                                                    name='products'
                                                    onChange={handleProductsChange}
                                                    styles={customStyles}
                                                    onBlur={formik.handleBlur}
                                                    placeholder="Select product variants for this special offer..."
                                                />
                                            </FormGroup>
                                        </Col>

                                        <Col md={12}>
                                            <FormGroup>
                                                <Label>Special Offer Description</Label>
                                                <Input
                                                    type='textarea'
                                                    rows={4}
                                                    placeholder='Enter Special Offer Description'
                                                    name='description'
                                                    onChange={formik.handleChange}
                                                    value={formik.values.description}
                                                    onBlur={formik.handleBlur}
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </Form>

            {/* Image Crop Modal */}
            <Modal isOpen={cropModalOpen} toggle={closeCropModal} size="lg" onClosed={closeCropModal}>
                <ModalHeader toggle={closeCropModal}>
                    Crop Image - {currentCropType === 'website' ? '11:1 Aspect Ratio' : '16:9 Aspect Ratio'}
                </ModalHeader>
                <ModalBody>
                    {imageToCrop && (
                        <div style={{ maxHeight: '70vh', overflow: 'auto' }}>
                            <ReactCrop
                                crop={crop}
                                onChange={(c) => setCrop(c)}
                                onComplete={(c) => setCompletedCrop(c)}
                                aspect={crop.aspect}
                            >
                                <img
                                    src={imageToCrop}
                                    onLoad={onImageLoad}
                                    alt="Crop preview"
                                    style={{ maxWidth: '100%' }}
                                />
                            </ReactCrop>
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={closeCropModal}>
                        Cancel
                    </Button>
                    <Button color="primary" onClick={handleCropComplete}>
                        Apply Crop
                    </Button>
                </ModalFooter>
            </Modal>
        </Fragment>
    )
}

export default CreateSpecial




// import { Fragment, useEffect, useState, useRef } from 'react'
// import { H3 } from '../../../../AbstractElements'
// import { Button, Card, CardBody, Col, Container, Form, FormGroup, Input, Label, Row, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap'
// import CreatableSelect from 'react-select/creatable';
// import axios from 'axios';
// import {productBaseURL } from '../../../../Services/api/baseURL';
// import { useFormik } from 'formik';
// import * as Yup from 'yup';
// import Swal from 'sweetalert2';
// import { useNavigate, useParams } from 'react-router';
// import DatePicker from "react-datepicker";
// import "react-datepicker/dist/react-datepicker.css";
// import ReactCrop from 'react-image-crop';
// import 'react-image-crop/dist/ReactCrop.css';

// function CreateSpecial() {
//     const { id } = useParams();
//     const navigate = useNavigate();
//     const [productData, setProductData] = useState([]);
//     const [selectedProducts, setSelectedProducts] = useState([]);
//     const [token, setToken] = useState(null);
//     const [isLoading, setIsLoading] = useState(false);
    
//     // Separate states for website and mobile banners
//     const [websiteBannerPreview, setWebsiteBannerPreview] = useState(null);
//     const [websiteBannerFile, setWebsiteBannerFile] = useState(null);
//     const [mobileBannerPreview, setMobileBannerPreview] = useState(null);
//     const [mobileBannerFile, setMobileBannerFile] = useState(null);

//     // Image cropping states
//     const [cropModalOpen, setCropModalOpen] = useState(false);
//     const [currentCropType, setCurrentCropType] = useState(null); // 'website' or 'mobile'
//     const [imageToCrop, setImageToCrop] = useState(null);
//     const [crop, setCrop] = useState({ unit: '%', width: 90, aspect: 11/1 });
//     const [completedCrop, setCompletedCrop] = useState(null);
//     const imgRef = useRef(null);
//     const previewCanvasRef = useRef(null);
    
//     const isDarkMode = localStorage.getItem("mix_background_layout")

//     const lightModeColors = {
//         primary: '#007bff',
//         secondary: '#6c757d',
//         text: '#212529',
//         background: '#ffffff',
//     };

//     const darkModeColors = {
//         primary: '#0d6efd',
//         secondary: '#3F444D',
//         text: 'white',
//         background: '#262932',
//     };

//     const colors = (isDarkMode === 'dark-only') ? darkModeColors : lightModeColors;

//     const customStyles = {
//         control: (provided) => ({
//             ...provided,
//             backgroundColor: colors.background,
//             borderColor: colors.secondary,
//         }),
//         option: (provided, state) => ({
//             ...provided,
//             backgroundColor: state.isSelected ? colors.primary : colors.background,
//             color: state.isSelected ? colors.text : colors.text,
//         }),
//     };

//     useEffect(() => {
//         UserData();
//     }, [])

//     const UserData = async () => {
//         const token = await JSON.parse(localStorage.getItem('token'));
//         setToken(token)
//     }

//     const validationSchema = Yup.object().shape({
//         name: Yup.string().required('Special Offer Name is required'),
//         description: Yup.string(),
//         validFrom: Yup.date().required('Valid From date is required'),
//         validTo: Yup.date()
//             .required('Valid To date is required')
//             .min(Yup.ref('validFrom'), 'Valid To date must be after Valid From date'),
//     });

//     const formik = useFormik({
//         initialValues: {
//             name: "",
//             description: "",
//             validFrom: new Date(),
//             validTo: new Date(),
//             products: [],
//             status: "active",
//         },
//         validationSchema: validationSchema,
//         onSubmit: async (values) => {
//             console.log(values, "special offer values")
//             try {
//                 if (new Date(values.validFrom) >= new Date(values.validTo)) {
//                     Swal.fire({
//                         icon: 'error',
//                         title: 'Valid To date must be after Valid From date',
//                     });
//                     return;
//                 }

//                 setIsLoading(true);

//                 // Create FormData for file upload
//                 const formData = new FormData();
//                 formData.append('name', values.name);
//                 formData.append('description', values.description);
//                 formData.append('validFrom', values.validFrom);
//                 formData.append('validTo', values.validTo);
//                 formData.append('status', values.status);

//                 if (values.products && values.products.length > 0) {
//                     const productIds = values.products.map(item => item.value);
//                     formData.append('products', JSON.stringify(productIds));
//                 }

//                 // Append website banner if exists (new file uploaded)
//                 if (websiteBannerFile) {
//                     formData.append('websiteBanner', websiteBannerFile);
//                 }

//                 // Append mobile banner if exists (new file uploaded)
//                 if (mobileBannerFile) {
//                     formData.append('mobileBanner', mobileBannerFile);
//                 }

//                 let res;
//                 if (id) {
//                     res = await axios.patch(`${productBaseURL}/products/specials/${id}`, formData, {
//                         headers: {
//                             Authorization: `${token}`,
//                             'Content-Type': 'multipart/form-data'
//                         }
//                     });
//                 } else {
//                     res = await axios.post(`${productBaseURL}/products/specials`, formData, {
//                         headers: {
//                             Authorization: `${token}`,
//                             'Content-Type': 'multipart/form-data'
//                         }
//                     });
//                 }

//                 if (res?.status === 200 || res?.status === 201) {
//                     Swal.fire({
//                         icon: "success",
//                         title: res?.data?.message || (id ? "Special Offer updated successfully" : "Special Offer created successfully")
//                     })
//                     if (id) {
//                         navigate(`/special`);
//                     } else {
//                         navigate(`/special`);
//                     }
//                     formik.resetForm();
//                     setSelectedProducts([]);
//                     setWebsiteBannerPreview(null);
//                     setWebsiteBannerFile(null);
//                     setMobileBannerPreview(null);
//                     setMobileBannerFile(null);
//                     setIsLoading(false);
//                 }
//             }
//             catch (error) {
//                 Swal.fire({
//                     icon: "error",
//                     title: error?.response?.data?.message || "An error occurred"
//                 })
//                 console.error(error);
//                 setIsLoading(false);
//             }
//         }
//     });

//     const fetchProductList = async () => {
//         const token = await JSON.parse(localStorage.getItem("token"))
//         try {
//             const response = await axios.get(`${productBaseURL}/products/get-all-products?page=1&limit=1000`, {
//                 headers: {
//                     Authorization: `${token}`,
//                 }
//             })
//             console.log(response, "all products list")
//             if (response?.data) {
//             const products = response?.data?.data.map(product => ({
//                 value: product._id,
//                 label: product.productName
//             }));
//             setProductData(products);
//         }
//         } catch (error) {
//             console.log(error)
//         }
//     }

//     useEffect(() => {
//         fetchProductList();

//         if (id) {
//             getEditOfferData();
//         }
//     }, [id]);

//     const handleProductsChange = (newValue, actionMeta) => {
//         setSelectedProducts(newValue);
//         formik.setFieldValue('products', newValue);
//     };

//     const validateImageAspectRatio = (file, expectedRatio, bannerType) => {
//         return new Promise((resolve) => {
//             const img = new Image();
//             img.onload = function() {
//                 const aspectRatio = this.width / this.height;
//                 const tolerance = 0.1;
                
//                 if (Math.abs(aspectRatio - expectedRatio) > tolerance) {
//                     // Show option to crop
//                     Swal.fire({
//                         icon: 'warning',
//                         title: 'Image Dimensions Mismatch',
//                         text: `Would you like to crop this image to match the required aspect ratio?`,
//                         showCancelButton: true,
//                         confirmButtonText: 'Yes, Crop It',
//                         cancelButtonText: 'Cancel'
//                     }).then((result) => {
//                         if (result.isConfirmed) {
//                             openCropModal(file, bannerType, expectedRatio);
//                         }
//                         resolve(false);
//                     });
//                 } else {
//                     resolve(true);
//                 }
//             };
//             img.src = URL.createObjectURL(file);
//         });
//     };

//     const openCropModal = (file, bannerType, aspectRatio) => {
//         setCurrentCropType(bannerType);
//         setCrop({ unit: '%', width: 90, aspect: aspectRatio });
//         setImageToCrop(URL.createObjectURL(file));
//         setCropModalOpen(true);
//     };

//     const handleWebsiteBannerChange = async (event) => {
//         const file = event.target.files[0];
//         if (file) {
//             // Validate file size
//             if (file.size > 5 * 1024 * 1024) {
//                 Swal.fire({
//                     icon: 'error',
//                     title: 'File too large',
//                     text: 'Please upload an image smaller than 5MB'
//                 });
//                 event.target.value = '';
//                 return;
//             }

//             // Validate 11:1 aspect ratio for website banner or offer to crop
//             const isValidRatio = await validateImageAspectRatio(file, 11, 'website');
//             if (isValidRatio) {
//                 setWebsiteBannerFile(file);
//                 setWebsiteBannerPreview(URL.createObjectURL(file));
//             } else {
//                 event.target.value = '';
//             }
//         }
//     };

//     const handleMobileBannerChange = async (event) => {
//         const file = event.target.files[0];
//         if (file) {
//             // Validate file size
//             if (file.size > 5 * 1024 * 1024) {
//                 Swal.fire({
//                     icon: 'error',
//                     title: 'File too large',
//                     text: 'Please upload an image smaller than 5MB'
//                 });
//                 event.target.value = '';
//                 return;
//             }

//             // Validate 16:9 aspect ratio for mobile banner or offer to crop
//             const isValidRatio = await validateImageAspectRatio(file, 16/9, 'mobile');
//             if (isValidRatio) {
//                 setMobileBannerFile(file);
//                 setMobileBannerPreview(URL.createObjectURL(file));
//             } else {
//                 event.target.value = '';
//             }
//         }
//     };

//     const onImageLoad = (e) => {
//         imgRef.current = e.currentTarget;
//     };

//     const generateCroppedImage = async () => {
//         if (!completedCrop || !imgRef.current) {
//             Swal.fire({
//                 icon: 'error',
//                 title: 'Please select a crop area'
//             });
//             return;
//         }

//         const image = imgRef.current;
//         const canvas = document.createElement('canvas');
//         const crop = completedCrop;

//         const scaleX = image.naturalWidth / image.width;
//         const scaleY = image.naturalHeight / image.height;
//         const ctx = canvas.getContext('2d');

//         canvas.width = crop.width * scaleX;
//         canvas.height = crop.height * scaleY;

//         ctx.drawImage(
//             image,
//             crop.x * scaleX,
//             crop.y * scaleY,
//             crop.width * scaleX,
//             crop.height * scaleY,
//             0,
//             0,
//             canvas.width,
//             canvas.height
//         );

//         return new Promise((resolve) => {
//             canvas.toBlob((blob) => {
//                 if (!blob) {
//                     console.error('Canvas is empty');
//                     return;
//                 }
//                 const file = new File([blob], `cropped-${currentCropType}-${Date.now()}.jpg`, {
//                     type: 'image/jpeg',
//                 });
//                 resolve(file);
//             }, 'image/jpeg', 0.95);
//         });
//     };

//     const handleCropComplete = async () => {
//         try {
//             const croppedFile = await generateCroppedImage();
            
//             if (currentCropType === 'website') {
//                 setWebsiteBannerFile(croppedFile);
//                 setWebsiteBannerPreview(URL.createObjectURL(croppedFile));
//             } else if (currentCropType === 'mobile') {
//                 setMobileBannerFile(croppedFile);
//                 setMobileBannerPreview(URL.createObjectURL(croppedFile));
//             }

//             setCropModalOpen(false);
//             setImageToCrop(null);
//             setCompletedCrop(null);
            
//             Swal.fire({
//                 icon: 'success',
//                 title: 'Image cropped successfully!',
//                 timer: 1500
//             });
//         } catch (error) {
//             console.error('Error cropping image:', error);
//             Swal.fire({
//                 icon: 'error',
//                 title: 'Failed to crop image'
//             });
//         }
//     };

//     const removeWebsiteBanner = () => {
//         setWebsiteBannerFile(null);
//         setWebsiteBannerPreview(null);
//         const input = document.getElementById('websiteBannerInput');
//         if (input) input.value = '';
//     };

//     const removeMobileBanner = () => {
//         setMobileBannerFile(null);
//         setMobileBannerPreview(null);
//         const input = document.getElementById('mobileBannerInput');
//         if (input) input.value = '';
//     };

//     const handleReset = () => {
//         formik.resetForm();
//         setSelectedProducts([]);
//         setWebsiteBannerPreview(null);
//         setWebsiteBannerFile(null);
//         setMobileBannerPreview(null);
//         setMobileBannerFile(null);
//     }

//     const getEditOfferData = async () => {
//         const token = await JSON.parse(localStorage.getItem("token"))
//         try {
//             const response = await axios.get(`${productBaseURL}/products/specials/${id}`, {
//                 headers: {
//                     Authorization: `${token}`,
//                 }
//             });
//             if (response?.data?.success) {
//                 let data = response?.data?.data;
//                 if (data) {
//                     formik.setFieldValue("name", data.name);
//                     formik.setFieldValue("description", data.description || "");
//                     formik.setFieldValue("validFrom", new Date(data.validFrom));
//                     formik.setFieldValue("validTo", new Date(data.validTo));
//                     formik.setFieldValue("status", data.status);

//                     if (data.products && data.products.length > 0) {
//                         const productOptions = data.products.map(item => ({
//                             value: item._id,
//                             label: item.productName
//                         }));
//                         setSelectedProducts(productOptions);
//                         formik.setFieldValue("products", productOptions);
//                     }

//                     // Set existing banner images - FIX: Check if bannerImages exist and are not null
//                     if (data.bannerImages?.website) {
//                         setWebsiteBannerPreview(data.bannerImages.website);
//                         // Don't set websiteBannerFile as it's an existing URL, not a new file
//                     }
//                     if (data.bannerImages?.mobile) {
//                         setMobileBannerPreview(data.bannerImages.mobile);
//                         // Don't set mobileBannerFile as it's an existing URL, not a new file
//                     }
//                 }
//             }
//         } catch (error) {
//             console.log(error)
//         }
//     }

//     return (
//         <Fragment>
//             <Form className='theme-form offer-form' onSubmit={formik.handleSubmit}>
//                 <Container fluid={true}>
//                     <div className='page-title'>
//                         <Row>
//                             <Col xs='6'>
//                                 <H3>{id ? "Update Special Offer" : "Add a New Special Offer"}</H3>
//                             </Col>
//                             <Col xs='6'>
//                                 <div className='text-right'>
//                                     {!id && <Button onClick={() => handleReset()} className='me-3 reset_btn'>Reset All</Button>}
//                                     <Button className='save_btn' type='submit' disabled={isLoading}>
//                                         {!isLoading && (id ? "Update Special Offer" : "Save Special Offer")}
//                                         {isLoading && (id ? "Updating..." : "Saving...")}
//                                     </Button>
//                                 </div>
//                             </Col>
//                         </Row>
//                     </div>
//                 </Container>
                
//                 <Container fluid={true}>
//                     <Row>
//                         <Col sm='12'>
//                             <Card>
//                                 <CardBody>
//                                     <Row>
//                                         <Col md={6}>
//                                             <FormGroup>
//                                                 <Label>Special Offer Name <span className='text-danger'>*</span></Label>
//                                                 <Input 
//                                                     type='text' 
//                                                     placeholder='Enter Special Offer Name' 
//                                                     name='name' 
//                                                     onChange={formik.handleChange} 
//                                                     value={formik.values.name} 
//                                                     onBlur={formik.handleBlur}
//                                                 />
//                                                 {formik.touched.name && formik.errors.name && (
//                                                     <span className="error text-danger">{formik.errors.name}</span>
//                                                 )}
//                                             </FormGroup>
//                                         </Col>

//                                         <Col md={6}>
//                                             <FormGroup>
//                                                 <Label>Status</Label>
//                                                 <Input 
//                                                     type='select' 
//                                                     name='status' 
//                                                     onChange={formik.handleChange} 
//                                                     value={formik.values.status} 
//                                                     onBlur={formik.handleBlur}
//                                                 >
//                                                     <option value="active">Active</option>
//                                                     <option value="inactive">Inactive</option>
//                                                 </Input>
//                                             </FormGroup>
//                                         </Col>

//                                         <Col md={6}>
//                                             <FormGroup>
//                                                 <Label>Valid From <span className='text-danger'>*</span></Label>
//                                                 <DatePicker
//                                                     className="form-control"
//                                                     selected={formik.values.validFrom}
//                                                     onChange={(date) => formik.setFieldValue('validFrom', date)}
//                                                     dateFormat="dd/MM/yyyy"
//                                                     minDate={new Date()}
//                                                 />
//                                                 {formik.touched.validFrom && formik.errors.validFrom && (
//                                                     <span className="error text-danger">{formik.errors.validFrom}</span>
//                                                 )}
//                                             </FormGroup>
//                                         </Col>

//                                         <Col md={6}>
//                                             <FormGroup>
//                                                 <Label>Valid To <span className='text-danger'>*</span></Label>
//                                                 <DatePicker
//                                                     className="form-control"
//                                                     selected={formik.values.validTo}
//                                                     onChange={(date) => formik.setFieldValue('validTo', date)}
//                                                     dateFormat="dd/MM/yyyy"
//                                                     minDate={formik.values.validFrom}
//                                                 />
//                                                 {formik.touched.validTo && formik.errors.validTo && (
//                                                     <span className="error text-danger">{formik.errors.validTo}</span>
//                                                 )}
//                                             </FormGroup>
//                                         </Col>

//                                         {/* Website Banner */}
//                                         <Col md={6}>
//                                             <FormGroup>
//                                                 <Label>Website Banner Image (11:1 Aspect Ratio)</Label>
//                                                 <Input 
//                                                     id="websiteBannerInput"
//                                                     type='file' 
//                                                     accept='image/*'
//                                                     onChange={handleWebsiteBannerChange}
//                                                     className="form-control"
//                                                 />
//                                                 <small className="form-text text-muted">
//                                                     Upload website banner with 11:1 aspect ratio (e.g., 1100x100px). Max size: 5MB
//                                                 </small>
                                                
//                                                 {websiteBannerPreview && (
//                                                     <div className="banner-preview mt-3">
//                                                         <div className="d-flex justify-content-between align-items-center mb-2">
//                                                             <Label>Website Banner Preview:</Label>
//                                                             <Button 
//                                                                 type="button" 
//                                                                 color="danger" 
//                                                                 size="sm" 
//                                                                 onClick={removeWebsiteBanner}
//                                                             >
//                                                                 Remove
//                                                             </Button>
//                                                         </div>
//                                                         <div 
//                                                             className="banner-preview-container"
//                                                             style={{
//                                                                 width: '100%',
//                                                                 maxWidth: '440px',
//                                                                 aspectRatio: '11/1',
//                                                                 border: '2px dashed #ddd',
//                                                                 borderRadius: '8px',
//                                                                 overflow: 'hidden',
//                                                                 position: 'relative'
//                                                             }}
//                                                         >
//                                                             <img 
//                                                                 src={websiteBannerPreview} 
//                                                                 alt="Website Banner Preview" 
//                                                                 style={{
//                                                                     width: '100%',
//                                                                     height: '100%',
//                                                                     objectFit: 'cover'
//                                                                 }}
//                                                             />
//                                                         </div>
//                                                     </div>
//                                                 )}
//                                             </FormGroup>
//                                         </Col>

//                                         {/* Mobile Banner */}
//                                         <Col md={6}>
//                                             <FormGroup>
//                                                 <Label>Mobile Banner Image (16:9 Aspect Ratio)</Label>
//                                                 <Input 
//                                                     id="mobileBannerInput"
//                                                     type='file' 
//                                                     accept='image/*'
//                                                     onChange={handleMobileBannerChange}
//                                                     className="form-control"
//                                                 />
//                                                 <small className="form-text text-muted">
//                                                     Upload mobile banner with 16:9 aspect ratio (e.g., 1920x1080px). Max size: 5MB
//                                                 </small>
                                                
//                                                 {mobileBannerPreview && (
//                                                     <div className="banner-preview mt-3">
//                                                         <div className="d-flex justify-content-between align-items-center mb-2">
//                                                             <Label>Mobile Banner Preview:</Label>
//                                                             <Button 
//                                                                 type="button" 
//                                                                 color="danger" 
//                                                                 size="sm" 
//                                                                 onClick={removeMobileBanner}
//                                                             >
//                                                                 Remove
//                                                             </Button>
//                                                         </div>
//                                                         <div 
//                                                             className="banner-preview-container"
//                                                             style={{
//                                                                 width: '100%',
//                                                                 maxWidth: '320px',
//                                                                 aspectRatio: '16/9',
//                                                                 border: '2px dashed #ddd',
//                                                                 borderRadius: '8px',
//                                                                 overflow: 'hidden',
//                                                                 position: 'relative'
//                                                             }}
//                                                         >
//                                                             <img 
//                                                                 src={mobileBannerPreview} 
//                                                                 alt="Mobile Banner Preview" 
//                                                                 style={{
//                                                                     width: '100%',
//                                                                     height: '100%',
//                                                                     objectFit: 'cover'
//                                                                 }}
//                                                             />
//                                                         </div>
//                                                     </div>
//                                                 )}
//                                             </FormGroup>
//                                         </Col>

//                                         <Col md={12}>
//                                             <FormGroup className='products_div'>
//                                                 <Label className="col-form-label">Select Products</Label>
//                                                 <CreatableSelect 
//                                                     className='my-select' 
//                                                     isMulti 
//                                                     options={productData} 
//                                                     value={selectedProducts}
//                                                     name='products' 
//                                                     onChange={handleProductsChange} 
//                                                     styles={customStyles} 
//                                                     onBlur={formik.handleBlur}
//                                                     placeholder="Select products for this special offer..."
//                                                 />
//                                             </FormGroup>
//                                         </Col>

//                                         <Col md={12}>
//                                             <FormGroup>
//                                                 <Label>Special Offer Description</Label>
//                                                 <Input 
//                                                     type='textarea' 
//                                                     rows={4} 
//                                                     placeholder='Enter Special Offer Description' 
//                                                     name='description' 
//                                                     onChange={formik.handleChange} 
//                                                     value={formik.values.description} 
//                                                     onBlur={formik.handleBlur}
//                                                 />
//                                             </FormGroup>
//                                         </Col>
//                                     </Row>
//                                 </CardBody>
//                             </Card>
//                         </Col>
//                     </Row>
//                 </Container>
//             </Form>

//             {/* Image Crop Modal */}
//             <Modal isOpen={cropModalOpen} toggle={() => setCropModalOpen(false)} size="lg">
//                 <ModalHeader toggle={() => setCropModalOpen(false)}>
//                     Crop Image - {currentCropType === 'website' ? '11:1 Aspect Ratio' : '16:9 Aspect Ratio'}
//                 </ModalHeader>
//                 <ModalBody>
//                     {imageToCrop && (
//                         <div style={{ maxHeight: '70vh', overflow: 'auto' }}>
//                             <ReactCrop
//                                 crop={crop}
//                                 onChange={(c) => setCrop(c)}
//                                 onComplete={(c) => setCompletedCrop(c)}
//                                 aspect={crop.aspect}
//                             >
//                                 <img
//                                     src={imageToCrop}
//                                     onLoad={onImageLoad}
//                                     alt="Crop preview"
//                                     style={{ maxWidth: '100%' }}
//                                 />
//                             </ReactCrop>
//                         </div>
//                     )}
//                 </ModalBody>
//                 <ModalFooter>
//                     <Button color="secondary" onClick={() => setCropModalOpen(false)}>
//                         Cancel
//                     </Button>
//                     <Button color="primary" onClick={handleCropComplete}>
//                         Apply Crop
//                     </Button>
//                 </ModalFooter>
//             </Modal>
//         </Fragment>
//     )
// }

// export default CreateSpecial

